import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { APG_GATEWAY_HOST } from 'config/app'
import ListingPage from '../../../containers/ListingPage'
import { Row, Cell } from '../../../components/Table'
import SingleDatePickerWrapper from '../../../components/SingleDatePickerWrapper'
import Toggle from '../../../components/Form/Inputs/Toggle'
import exportFromJSON from 'export-from-json'
import moment from 'moment'
import API from '../../../lib/api'
import './style.css'

const DATE_FORMAT = 'YYYY-MM-DD'

export default class PickerAdmin extends Component {
    constructor(props) {
        super(props)
        const today = moment().format(DATE_FORMAT)

        // Read URL query parameters to initialize dates
        const urlParams = new URLSearchParams(window.location.search)
        const pickupDateFromUrl = urlParams.get('pickupDate')

        let pickupDateStart = today
        let pickupDateEnd = today

        if (pickupDateFromUrl) {
            // Parse format: "2025-12-31 00:00:00..2025-12-31 23:59:59"
            const dateRange = pickupDateFromUrl.split('..')
            if (dateRange.length === 2) {
                const startDateStr = dateRange[0].trim()
                const endDateStr = dateRange[1].trim()
                // Extract date part (YYYY-MM-DD) from "YYYY-MM-DD HH:mm:ss"
                const startDate = startDateStr.split(' ')[0]
                const endDate = endDateStr.split(' ')[0]

                // Validate dates
                if (moment(startDate, DATE_FORMAT, true).isValid()) {
                    pickupDateStart = startDate
                }
                if (moment(endDate, DATE_FORMAT, true).isValid()) {
                    pickupDateEnd = endDate
                }
            }
        }

        this.state = {
            pickupDateStart,
            pickupDateEnd,
            data: [],
        }
    }

    handleDownload = () => {
        const { data, pickupDateStart, pickupDateEnd } = this.state

        const exportData = data.map(item => ({
            'Pickup Date': item.pickupDate,
            'Picklist Number': item.id,
            'Vendor Code': item.seller?.vendorCode,
            'Vendor Name': item.seller?.sellerName,
            'Transporter ID': item.transporter?.id || '',
            'Status': item.status,
            'Supplier Submit Status': item.submissionStatus?.warehouseSubmission ? 'Y' : 'N',
            'Transporter Submit Status': item.submissionStatus?.transporterSubmission ? 'Y' : 'N',
            'Transporter Submit Timestamp': item.submissionStatus?.transporterSubmittedAt || '',
        }))

        exportFromJSON({
            data: exportData,
            fileName: `Picker_Admin_${pickupDateStart}_to_${pickupDateEnd}`,
            exportType: exportFromJSON.types.xls,
        })
    }

    handleTransporterSubmission = async (item, checked) => {
        const api = new API({
            url: `${APG_GATEWAY_HOST}/seller-order-service/picklists/admin`,
        })

        const requestBody = {
            trackingID: item.trackingID || `PA${String(item.id).padStart(8, '0')}`,
            transporterDriverID: item.transporter?.id || '',
            picklistID: item.id,
            vendorCode: item.seller?.vendorCode || item.vendorCode,
            storeID: item.storeID || 4,
            clientID: item.clientID || '',
            transporterSubmission: checked ? 'on' : 'off',
        }

        try {
            await api.post(requestBody)
            const now = moment().format('YYYY-MM-DD HH:mm:ss')
            this.setState(prevState => ({
                data: prevState.data.map(d =>
                    d.id === item.id
                        ? {
                            ...d,
                            submissionStatus: {
                                ...(d.submissionStatus || {}),
                                transporterSubmission: checked,
                                transporterSubmittedAt: checked ? now : null
                            }
                        }
                        : d
                )
            }))
        } catch (error) {
            console.error('Error updating transporter submission:', error)
            // Revert the toggle on error
            this.setState(prevState => ({
                data: prevState.data.map(d =>
                    d.id === item.id
                        ? {
                            ...d,
                            submissionStatus: {
                                ...(d.submissionStatus || {}),
                                transporterSubmission: !checked
                            }
                        }
                        : d
                )
            }))
            // You might want to show an error message to the user here
            alert(`Failed to update transporter submission: ${error.message || 'Unknown error'}`)
        }
    }

    getTableProperties = () => {
        return {
            headers: [
                'Picklist No.',
                'Vendor Code/Vendor Name',
                'Pickup Date',
                'Transporter ID',
                'Status',
                'Supplier Submission',
                'Transporter Submission',
                'Actions',
            ],
            row: (item) => {
                // Find data in local state to ensure toggles reflect UI updates
                const localItem = this.state.data.find(d => d.id === item.id)
                if (!localItem) {
                    return null
                }
                const id = localItem.id

                return (
                    <Row key={id}>
                        <Cell>
                            <Link
                                to={`/marketplace/picker-admin/${id}`}
                                className="picklist-link"
                            >
                                {id}
                            </Link>
                        </Cell>
                        <Cell>
                            <div className="cell-text-bold">{localItem.seller?.vendorCode}</div>
                            <div>{localItem.seller?.sellerName}</div>
                        </Cell>
                        <Cell>
                            <span>{localItem.pickupDate}</span>
                        </Cell>
                        <Cell>
                            <span>{localItem.transporter?.id || '-'}</span>
                        </Cell>
                        <Cell>
                            <span className={`status-badge status-${localItem.status?.toLowerCase()?.replace(' ', '-')}`}>
                                {localItem.status}
                            </span>
                        </Cell>
                        <Cell>
                            <div className="submission-toggle">
                                <Toggle
                                    name={`supplier-${id}`}
                                    value={!!localItem.submissionStatus?.warehouseSubmission}
                                    icons={false}
                                    disabled={localItem.status === 'PACKED'}
                                    onChange={(checked) => {
                                        // Prevent unchecking supplier submission when status is PACKED
                                        if (localItem.status === 'PACKED' && !checked) {
                                            return
                                        }
                                        this.setState(prevState => ({
                                            data: prevState.data.map(d =>
                                                d.id === id
                                                    ? {
                                                        ...d,
                                                        submissionStatus: {
                                                            ...(d.submissionStatus || {}),
                                                            warehouseSubmission: checked
                                                        }
                                                    }
                                                    : d
                                            )
                                        }))
                                    }}
                                />
                            </div>
                        </Cell>
                        <Cell>
                            <div className="submission-toggle">
                                <Toggle
                                    name={`transporter-${id}`}
                                    value={!!localItem.submissionStatus?.transporterSubmission}
                                    icons={false}
                                    disabled={!!localItem.submissionStatus?.transporterSubmission}
                                    onChange={(checked) => {
                                        // Prevent unchecking transporter submission once it's enabled
                                        if (localItem.submissionStatus?.transporterSubmission && !checked) {
                                            return
                                        }

                                        // Optimistically update UI
                                        const now = moment().format('YYYY-MM-DD HH:mm:ss')
                                        this.setState(prevState => ({
                                            data: prevState.data.map(d =>
                                                d.id === id
                                                    ? {
                                                        ...d,
                                                        submissionStatus: {
                                                            ...(d.submissionStatus || {}),
                                                            transporterSubmission: checked,
                                                            transporterSubmittedAt: checked ? now : null
                                                        }
                                                    }
                                                    : d
                                            )
                                        }))
                                        // Make API call
                                        this.handleTransporterSubmission(localItem, checked)
                                    }}
                                />
                                {localItem.submissionStatus?.transporterSubmittedAt && (
                                    <span className="submission-time">{localItem.submissionStatus.transporterSubmittedAt}</span>
                                )}
                            </div>
                        </Cell>
                        <Cell>
                            <div className="action-buttons">
                                <Link
                                    to={`/marketplace/picker-admin/${id}/tu-list`}
                                    className="btn-action btn-list"
                                    title="TU List"
                                >
                                    TU List
                                </Link>
                                <Link
                                    to={`/marketplace/picker-admin/${id}/detail?pickupDate=${localItem.pickupDate}`}
                                    className="btn-action btn-detail"
                                    title="Detail"
                                >
                                    Detail
                                </Link>
                            </div>
                        </Cell>
                    </Row>
                )
            },
        }
    }

    render() {
        const { menu } = this.props

        return (
            <ListingPage
                {...this.props}
                menu={menu}
                className="picker-admin-page"
                title="Picker Admin Dashboard"
                api={{
                    url: `${APG_GATEWAY_HOST}/seller-order-service/picklists/admin`,
                    params: {
                        perPage: 20,
                        storeID: 4,
                        vendorCode: '*',
                        sort: 'vendor_code',
                        pickupDate: `${this.state.pickupDateStart} 00:00:00..${this.state.pickupDateEnd} 23:59:59`,
                    },
                    transform: (response) => {
                        if (response && response.data) {
                            // Handle new response structure with picklists array
                            let picklists = []
                            if (response.data.picklists && Array.isArray(response.data.picklists)) {
                                picklists = response.data.picklists
                            } else if (Array.isArray(response.data)) {
                                picklists = response.data
                            }

                            // Ensure picklists is always an array
                            if (!Array.isArray(picklists)) {
                                picklists = []
                            }

                            // Ensure supplier submission is checked when status is PACKED
                            const processedData = picklists.map(item => {
                                if (item && item.status === 'PACKED') {
                                    return {
                                        ...item,
                                        submissionStatus: {
                                            ...(item.submissionStatus || {}),
                                            warehouseSubmission: true
                                        }
                                    }
                                }
                                return item
                            }).filter(Boolean) // Remove any null/undefined items

                            // Check if we need to update state
                            const serverIds = processedData.map(d => d.id).sort().join(',')
                            const localIds = this.state.data.map(d => d.id).sort().join(',')

                            // Check if any PACKED items need warehouseSubmission to be set to true
                            const needsUpdate = processedData.some(item =>
                                item.status === 'PACKED' &&
                                !this.state.data.find(d => d.id === item.id && d.submissionStatus?.warehouseSubmission)
                            )

                            if (this.state.data.length === 0 || serverIds !== localIds || needsUpdate) {
                                // Use a stable update to avoid infinite loops
                                setTimeout(() => {
                                    this.setState({ data: processedData })
                                }, 0)
                            }

                            // ListingPage expects transform to return an array directly
                            // It assigns the result to data.items
                            return Array.isArray(processedData) ? processedData : []
                        }
                        return []
                    },
                }}
                tableProperties={this.getTableProperties()}
                filters={{
                    transformFilter: (filters) => {
                        // Always use component state dates to ensure consistency
                        return {
                            ...filters,
                            pickupDate: `${this.state.pickupDateStart} 00:00:00..${this.state.pickupDateEnd} 23:59:59`
                        }
                    },
                    component: ({ onSubmit }) => (
                        <div className="picker-admin-filters-container">
                            <div className="filter-group">
                                <div className="filter-label">Pickup Date Start:</div>
                                <div className="date-picker-wrapper">
                                    <SingleDatePickerWrapper
                                        date={this.state.pickupDateStart}
                                        onDateChange={(date) => this.setState({ pickupDateStart: date })}
                                        isOutsideRange={false}
                                        allowAllDates={true}
                                        placeholder="Select start date"
                                    />
                                </div>
                            </div>
                            <div className="filter-group">
                                <div className="filter-label">Pickup Date End:</div>
                                <div className="date-picker-wrapper">
                                    <SingleDatePickerWrapper
                                        date={this.state.pickupDateEnd}
                                        onDateChange={(date) => this.setState({ pickupDateEnd: date })}
                                        isOutsideRange={false}
                                        allowAllDates={true}
                                        placeholder="Select end date"
                                    />
                                </div>
                                <button
                                    className="primary button filter-go-button"
                                    onClick={() => onSubmit({
                                        pickupDate: `${this.state.pickupDateStart} 00:00:00..${this.state.pickupDateEnd} 23:59:59`
                                    })}
                                >
                                    Go
                                </button>
                            </div>
                        </div>
                    ),
                    showFiltersOnLoad: false,
                }}
                headerActions={() => (
                    <div className="header-actions-container">
                        <button
                            className="button download-template-btn"
                            onClick={this.handleDownload}
                            title="Download"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20">
                                <g fill="none" fillRule="evenodd">
                                    <path d="M-3-6h30v30H-3z" opacity=".05" />
                                    <g stroke="#80959D">
                                        <path d="M1 15v4h22v-4M8 8l4.495 4L17 8M12.5 11.5V0" />
                                    </g>
                                </g>
                            </svg>
                        </button>
                    </div>
                )}
                primaryKey="id"
            />
        )
    }
}
